// Global Add-Invoice modal.
//
// Amounts: the user types dollars, we store cents (amount_cents). InvoicePortal
// + activity feed both read amount_cents, so storing dollars-as-cents would be
// a 100× display bug.

import { useState } from 'react';
import { useApp } from '@/context/AppContext';

export default function AddInvoiceModal({ data = {} }) {
  const { customers, jobs, addInvoice, closeModal, cloudMode } = useApp();
  const today      = new Date().toISOString().slice(0, 10);
  const defaultDue = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

  // Hide optimistic customers/jobs whose cloud insert is still pending — their
  // temp id (Date.now()) would FK-violate against the persisted row id.
  const isTempId = (id) => typeof id === 'number' && id > 1e10;
  const selectableCustomers = cloudMode ? customers.filter(c => !isTempId(c.id)) : customers;
  const selectableJobs      = cloudMode ? jobs.filter(j => !isTempId(j.id))      : jobs;

  const [form, setForm] = useState({
    customerId: data.customerId ? String(data.customerId) : '',
    jobId:      data.jobId      ? String(data.jobId)      : '',
    service:    data.service    || '',
    amount:     data.amount     || '',
    date:       today,
    due:        defaultDue,
  });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const amountDollars = Number(form.amount);
    if (!form.customerId || !form.service.trim() || !(amountDollars > 0)) return;
    addInvoice({
      customerId: Number(form.customerId),
      jobId:      form.jobId ? Number(form.jobId) : null,
      service:    form.service.trim(),
      amount:     Math.round(amountDollars * 100),  // → cents
      date:       form.date,
      due:        form.due,
      status:     'sent',
    });
    closeModal();
  };

  return (
    <div className="modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-head">
        <span className="modal-title">New Invoice</span>
        <button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          <div className="field">
            <label>Customer *</label>
            <select value={form.customerId} onChange={e => set('customerId', e.target.value)} required>
              <option value="">Select customer…</option>
              {selectableCustomers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {selectableCustomers.length === 0 && (
              <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 4 }}>
                No customers yet — add one from the Customers screen.
              </div>
            )}
          </div>
          <div className="field">
            <label>Job (optional)</label>
            <select value={form.jobId} onChange={e => set('jobId', e.target.value)}>
              <option value="">No specific job</option>
              {selectableJobs.map(j => (
                <option key={j.id} value={j.id}>{j.jobNumber} — {j.projectName}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Description / Service *</label>
            <input
              value={form.service}
              onChange={e => set('service', e.target.value)}
              placeholder="e.g. Progress billing 50% — Pinnacle Warehouse"
              required
            />
          </div>
          <div className="form-row">
            <div className="field">
              <label>Amount (USD) *</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.amount}
                onChange={e => set('amount', e.target.value)}
                placeholder="0.00"
                required
              />
            </div>
            <div className="field">
              <label>Issued</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
            </div>
            <div className="field">
              <label>Due</label>
              <input type="date" value={form.due} onChange={e => set('due', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button type="submit" className="btn btn-primary">Create Invoice</button>
        </div>
      </form>
    </div>
  );
}
